import React, { useEffect, useState } from 'react'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap'
import BootstrapTable from 'react-bootstrap-table-next'
import { Link } from 'react-router-dom'
import { api } from '../../utils'
import ClienteSelect from '../../components/molecules/ClienteSelect'
import Header from '../../components/Headers/Header'
import SetTitle from '../../utils/set_title'

const ProjectList = () => {
  SetTitle('Link Building Projects')
  const [projects, setProjects] = useState([])
  const [clienti, setClienti] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [selectedClientId, setSelectedClientId] = useState('')
  const [projectFilterClientId, setProjectFilterClientId] = useState('')
  const [appliedProjectClientId, setAppliedProjectClientId] = useState('')
  const [creating, setCreating] = useState(false)
  const [selectedProjects, setSelectedProjects] = useState([])

  useEffect(() => {
    fetchClienti()
  }, [])

  useEffect(() => {
    fetchProjects(appliedProjectClientId)
  }, [appliedProjectClientId])

  const extractCollection = (payload) => {
    if (Array.isArray(payload)) {
      return payload
    }
    return payload?.results || []
  }

  const fetchProjects = async (clienteId = appliedProjectClientId) => {
    try {
      setLoading(true)
      const response = await api.get('link-building/projects/', {
        params: clienteId ? { cliente: clienteId } : {},
      })
      const nextProjects = extractCollection(response.data)
      setProjects(nextProjects)
      setSelectedProjects((prev) =>
        prev.filter((selectedId) => nextProjects.some((project) => project.id === selectedId))
      )
    } catch (error) {
      console.error('Error fetching projects:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchClienti = async () => {
    try {
      const response = await api.get('clienti/')
      setClienti(extractCollection(response.data))
    } catch (error) {
      console.error('Error fetching clienti:', error)
    }
  }

  const toggleModal = () => {
    setModalOpen(!modalOpen)
    setNewProjectName('')
    setSelectedClientId('')
  }

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return
    if (!selectedClientId) {
      alert('Seleziona un cliente.')
      return
    }
    setCreating(true)
    try {
      const payload = { name: newProjectName, cliente_id: Number(selectedClientId) }
      await api.post('link-building/projects/', payload)
      toggleModal()
      fetchProjects(appliedProjectClientId)
    } catch (error) {
      console.error('Error creating project:', error)
      alert(error.response?.data?.error || 'Error creating project.')
    } finally {
      setCreating(false)
    }
  }

  const handleDeleteProject = async (id, name) => {
    if (
      window.confirm(
        `Sei sicuro di voler eliminare il progetto "${name}"? Questa operazione eliminerà anche tutti i link associati.`
      )
    ) {
      try {
        await api.delete(`link-building/projects/${id}/`)
        fetchProjects(appliedProjectClientId)
        setSelectedProjects((prev) => prev.filter((selectedId) => selectedId !== id))
      } catch (error) {
        console.error('Error deleting project:', error)
        alert(error.response?.data?.error || "Errore durante l'eliminazione del progetto.")
      }
    }
  }

  const handleBulkDelete = async () => {
    if (selectedProjects.length === 0) return
    if (
      window.confirm(
        `Sei sicuro di voler eliminare i ${selectedProjects.length} progetti selezionati? Questa operazione eliminerà anche tutti i link associati.`
      )
    ) {
      try {
        await api.post('link-building/projects/bulk_delete/', { ids: selectedProjects })
        setSelectedProjects([])
        fetchProjects(appliedProjectClientId)
      } catch (error) {
        console.error('Error deleting projects in bulk:', error)
        alert(error.response?.data?.error || "Errore durante l'eliminazione multipla.")
      }
    }
  }

  const nameFormatter = (cell, row) => {
    return <Link to={`/link-building/projects/${row.id}`}>{cell}</Link>
  }

  const actionFormatter = (cell, row) => {
    return (
      <Button
        color="danger"
        size="sm"
        outline
        onClick={() => handleDeleteProject(row.id, row.name)}
        title="Elimina"
      >
        <i className="fas fa-trash" />
      </Button>
    )
  }

  const columns = [
    { dataField: 'id', text: 'ID', sort: true, hidden: true },
    { dataField: 'name', text: 'Project Name', sort: true, formatter: nameFormatter },
    {
      dataField: 'cliente',
      text: 'Cliente',
      formatter: (_, row) => row?.cliente?.nome || '',
    },
    { dataField: 'created_at', text: 'Created At', sort: true },
    {
      dataField: 'actions',
      text: 'Azioni',
      isDummyField: true,
      formatter: actionFormatter,
      headerStyle: { width: '80px', textAlign: 'center' },
      style: { textAlign: 'center' },
    },
  ]

  const selectRow = {
    mode: 'checkbox',
    clickToSelect: false,
    selected: selectedProjects,
    onSelect: (row, isSelect) => {
      if (isSelect) {
        setSelectedProjects([...selectedProjects, row.id])
      } else {
        setSelectedProjects(selectedProjects.filter((x) => x !== row.id))
      }
    },
    onSelectAll: (isSelect, rows) => {
      const ids = rows.map((r) => r.id)
      if (isSelect) {
        const newSelected = [...new Set([...selectedProjects, ...ids])]
        setSelectedProjects(newSelected)
      } else {
        setSelectedProjects(selectedProjects.filter((x) => !ids.includes(x)))
      }
    },
  }

  const handleApplyFilters = () => {
    setSelectedProjects([])
    setAppliedProjectClientId(projectFilterClientId)
  }

  const handleClearFilters = () => {
    setSelectedProjects([])
    setProjectFilterClientId('')
    setAppliedProjectClientId('')
  }

  return (
    <Header
      iconClass="ni ni-world-2 text-white"
      title="Link Building Projects"
      description="Crea e gestisci progetti di Link Building associando domini del database esterno e monitorando per ogni pubblicazione presenza del backlink, indicizzabilita della pagina e attributi del link."
    >
      <div className="container-fluid">
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Link Building Projects</h3>
                <div>
                  {selectedProjects.length > 0 && (
                    <Button color="danger" size="sm" onClick={handleBulkDelete} className="mr-3">
                      <i className="fas fa-trash mr-2" /> Elimina Selezionati (
                      {selectedProjects.length})
                    </Button>
                  )}
                  <Button color="success" size="sm" onClick={toggleModal}>
                    <i className="fas fa-plus mr-2" /> Create Project
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <Row className="align-items-end mb-3">
                  <Col md={4}>
                    <ClienteSelect
                      id="link-building-cliente-filter"
                      label="Cliente"
                      clienti={clienti}
                      selectedClientId={projectFilterClientId}
                      onChange={setProjectFilterClientId}
                      placeholder="Filtra per cliente"
                      isClearable
                    />
                  </Col>
                  <Col md={8} className="text-right pb-3">
                    <Button color="light" className="mr-2" onClick={handleClearFilters}>
                      Clear Filters
                    </Button>
                    <Button color="primary" onClick={handleApplyFilters}>
                      Apply
                    </Button>
                  </Col>
                </Row>
                {loading ? (
                  <p>Loading...</p>
                ) : (
                  <BootstrapTable
                    bootstrap4
                    keyField="id"
                    data={projects}
                    columns={columns}
                    selectRow={selectRow}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={modalOpen} toggle={toggleModal}>
          <ModalHeader toggle={toggleModal}>Create New Link Building Project</ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="projectName">Project Name</Label>
              <Input
                type="text"
                id="projectName"
                placeholder="Enter project name..."
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
              />
            </FormGroup>
            <ClienteSelect
              id="projectClient"
              label="Cliente"
              clienti={clienti}
              selectedClientId={selectedClientId}
              onChange={setSelectedClientId}
              isRequired
              showCreateLink
            />
            {clienti.length === 0 && (
              <p className="text-muted mb-0">
                Nessun cliente disponibile per l&apos;utente corrente.
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={handleCreateProject}
              disabled={creating || !newProjectName.trim()}
            >
              {creating ? 'Creating...' : 'Create'}
            </Button>{' '}
            <Button color="secondary" onClick={toggleModal} disabled={creating}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </Header>
  )
}

export default ProjectList
